import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Inquiry } from 'dashboard/models/inquiry';
import { MetricsInquiryVolume } from 'dashboard/models/metrics';
import HighlightCard, {
  formatInquiryVolumeMetrics,
} from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/HighlightCard';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

export interface DashboardHomeInquiriesActivityProps {
  inquiries: Inquiry[];
  inquiryAnalyticsVolume: MetricsInquiryVolume;
  inquiryAnalyticsVolumeDelta: MetricsInquiryVolume;
  isInquiriesLoading: boolean;
  isInquiryAnalyticsLoading: boolean;
  handleLoadInquiryAnalytics: () => void;
}

const METRICS_CARD_MIN_WIDTH = 200;

const DashboardHomeInquiriesActivity = ({
  inquiries,
  inquiryAnalyticsVolume,
  inquiryAnalyticsVolumeDelta,
  isInquiriesLoading,
  isInquiryAnalyticsLoading,
  handleLoadInquiryAnalytics,
}: DashboardHomeInquiriesActivityProps) => {
  const isInquiryVolumeDeltaEnabled = inquiryAnalyticsVolumeDelta != null;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const formattedMetrics = formatInquiryVolumeMetrics(inquiryAnalyticsVolume, null);
  const formattedDeltaMetrics = isInquiryVolumeDeltaEnabled
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      formatInquiryVolumeMetrics(inquiryAnalyticsVolumeDelta, null)
    : null;
  const domain = isInquiryVolumeDeltaEnabled ? inquiryAnalyticsVolumeDelta.domain : null;

  useEffect(() => {
    handleLoadInquiryAnalytics();
  }, [handleLoadInquiryAnalytics]);

  return (
    <>
      {isInquiriesLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <MetricsCards>
            <HighlightCard
              label='Started rate'
              toolTipCopy='% of created inquiries that started the inquiry flow (in past 14 days)'
              minWidth={METRICS_CARD_MIN_WIDTH}
              isLoading={isInquiryAnalyticsLoading}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.started}
              previousPeriodMetricValue={formattedDeltaMetrics?.started}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
            />
            <HighlightCard
              label='Finished rate'
              toolTipCopy='% of started inquiries that reached the end of the inquiry flow (in past 14 days)'
              minWidth={METRICS_CARD_MIN_WIDTH}
              isLoading={isInquiryAnalyticsLoading}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.finished}
              previousPeriodMetricValue={formattedDeltaMetrics?.finished}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
            />
            <HighlightCard
              label='Success rate'
              toolTipCopy='% of finished inquiries that successfully passed all of the requirements configured in the inquiry template (in past 14 days)'
              minWidth={METRICS_CARD_MIN_WIDTH}
              isLoading={isInquiryAnalyticsLoading}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.success}
              previousPeriodMetricValue={formattedDeltaMetrics?.success}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
            />
          </MetricsCards>
          <Spacer height={1} />
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Inquiry ID</TableHeaderCell>
                <TableHeaderCell>Reference ID</TableHeaderCell>
                <TableHeaderCell>Created at</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inquiries.map((inquiry) => (
                <TableRow
                  key={`inquiry-${inquiry.id}`}
                  as={Link}
                  to={`/dashboard/inquiries/${inquiry.id}`}
                >
                  <TableCellStyled>
                    {inquiry.attributes['name-full-localized'] ?? inquiry.attributes['name-full']}
                  </TableCellStyled>
                  <TableCellStyled>{inquiry.id}</TableCellStyled>
                  <TableCellStyled>{inquiry.attributes['reference-id']}</TableCellStyled>
                  <TableCellStyled>
                    {DateTime.fromISO(inquiry.attributes['created-at']).toFormat(
                      DATE_AND_TIME_FORMAT,
                    )}
                  </TableCellStyled>
                  <TableCellStyled>
                    <InquiryStatusSticker status={inquiry.attributes.status} />
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {inquiries.length === 0 && (
            <TableBodyZeroState>No recent Inquiries created.</TableBodyZeroState>
          )}
        </>
      )}
    </>
  );
};

const MetricsCards = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[4]};
`;

const TableBodyZeroState = styled('div')`
  margin-top: ${Spacing[2]};
  padding: ${Spacing[7]} ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  text-align: center;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
`;

const TableCellStyled = styled(TableCell)`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default DashboardHomeInquiriesActivity;
